import { useState } from "react";
import { ChevronDown, ChevronUp, X, SlidersHorizontal } from "lucide-react";
import type { Companion } from "@shared/schema";

// Filter option definitions
const SERVICE_OPTIONS = [
  "GFE", "OWO", "CIM", "COB", "CIF", "FK", "DFK",
  "69", "A-Level", "Duo", "Party Girl", "DT", "Massage",
  "Tantric", "Overnight", "Dinner Date", "Travel",
];

const HAIR_OPTIONS = ["Blonde", "Brunette", "Black", "Red", "Auburn", "Other"];
const BREAST_OPTIONS = ["Small (A-B)", "Medium (C-D)", "Large (DD+)", "Natural", "Enhanced"];
const DRESS_OPTIONS = ["6", "8", "10", "12", "14", "16"];
const NATIONALITY_OPTIONS = [
  "British", "Eastern European", "Latin", "Asian", "Mixed", "Other",
];
const BODY_TYPE_OPTIONS = ["Slim", "Athletic", "Curvy", "Petite", "Average"];
const AGE_RANGES = [
  { label: "18-21", min: 18, max: 21 },
  { label: "22-25", min: 22, max: 25 },
  { label: "26-30", min: 26, max: 30 },
  { label: "31-35", min: 31, max: 35 },
  { label: "36-40", min: 36, max: 40 },
  { label: "40+", min: 40, max: 99 },
];
const PRICE_RANGES = [
  { label: "Under £150", min: 0, max: 150 },
  { label: "£150 - £200", min: 150, max: 200 },
  { label: "£200 - £300", min: 200, max: 300 },
  { label: "£300 - £500", min: 300, max: 500 },
  { label: "£500+", min: 500, max: 99999 },
];

export interface FilterState {
  services: string[];
  hairColor: string[];
  breastSize: string[];
  dressSize: string[];
  nationality: string[];
  bodyType: string[];
  ageRange: { min: number; max: number } | null;
  priceRange: { min: number; max: number } | null;
  availability: string[];
}

const INITIAL_FILTERS: FilterState = {
  services: [],
  hairColor: [],
  breastSize: [],
  dressSize: [],
  nationality: [],
  bodyType: [],
  ageRange: null,
  priceRange: null,
  availability: [],
};

function parsePrice(price: string): number {
  const match = price.match(/(\d+)/);
  return match ? parseInt(match[1], 10) : 0;
}

export function applyFilters(companions: Companion[], filters: FilterState): Companion[] {
  return companions.filter((c) => {
    // Services filter
    if (filters.services.length > 0) {
      const compServices = (c as any).services || c.tags || [];
      if (!filters.services.some((s) => compServices.some((cs: string) => cs.toLowerCase().includes(s.toLowerCase())))) {
        return false;
      }
    }

    // Hair color
    if (filters.hairColor.length > 0) {
      const hair = ((c as any).hairColor || "").toLowerCase();
      if (!filters.hairColor.some((h) => hair.includes(h.toLowerCase()))) return false;
    }

    // Breast size
    if (filters.breastSize.length > 0) {
      const breast = ((c as any).breastSize || "").toLowerCase();
      if (!filters.breastSize.some((b) => breast.toLowerCase().includes(b.toLowerCase()))) return false;
    }

    // Dress size
    if (filters.dressSize.length > 0) {
      const dress = (c as any).dressSize || "";
      if (!filters.dressSize.includes(dress)) return false;
    }

    // Nationality
    if (filters.nationality.length > 0) {
      const nat = ((c as any).nationality || "").toLowerCase();
      if (!filters.nationality.some((n) => nat.includes(n.toLowerCase()))) return false;
    }

    // Body type
    if (filters.bodyType.length > 0) {
      const body = ((c as any).bodyType || "").toLowerCase();
      if (!filters.bodyType.some((b) => body.includes(b.toLowerCase()))) return false;
    }

    // Age range
    if (filters.ageRange) {
      if (c.age < filters.ageRange.min || c.age > filters.ageRange.max) return false;
    }

    // Price range
    if (filters.priceRange) {
      const price = parsePrice(c.price);
      if (price < filters.priceRange.min || price > filters.priceRange.max) return false;
    }

    // Availability
    if (filters.availability.length > 0) {
      if (!filters.availability.includes(c.availability)) return false;
    }

    return true;
  });
}

function getActiveFilterCount(filters: FilterState): number {
  let count = 0;
  if (filters.services.length) count += filters.services.length;
  if (filters.hairColor.length) count += filters.hairColor.length;
  if (filters.breastSize.length) count += filters.breastSize.length;
  if (filters.dressSize.length) count += filters.dressSize.length;
  if (filters.nationality.length) count += filters.nationality.length;
  if (filters.bodyType.length) count += filters.bodyType.length;
  if (filters.ageRange) count += 1;
  if (filters.priceRange) count += 1;
  if (filters.availability.length) count += filters.availability.length;
  return count;
}

// Collapsible filter section
function FilterSection({
  title,
  children,
  defaultOpen = false,
}: {
  title: string;
  children: React.ReactNode;
  defaultOpen?: boolean;
}) {
  const [open, setOpen] = useState(defaultOpen);

  return (
    <div className="border-b border-gray-800">
      <button
        type="button"
        onClick={() => setOpen(!open)}
        className="flex items-center justify-between w-full py-3 px-1 text-left"
      >
        <span className="text-[10px] uppercase tracking-widest font-bold text-gray-300">
          {title}
        </span>
        {open ? (
          <ChevronUp size={14} className="text-gray-600" />
        ) : (
          <ChevronDown size={14} className="text-gray-600" />
        )}
      </button>
      {open && <div className="pb-4 px-1">{children}</div>}
    </div>
  );
}

// Checkbox option
function CheckOption({
  label,
  checked,
  onChange,
}: {
  label: string;
  checked: boolean;
  onChange: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onChange}
      className="flex items-center gap-2.5 py-1 cursor-pointer group w-full text-left"
    >
      <div
        className={`w-4 h-4 rounded border flex-shrink-0 flex items-center justify-center transition-all ${
          checked
            ? "bg-[#D4AF37] border-[#D4AF37]"
            : "border-gray-700 group-hover:border-gray-500"
        }`}
      >
        {checked && (
          <svg width="10" height="8" viewBox="0 0 10 8" fill="none">
            <path d="M1 4L3.5 6.5L9 1" stroke="black" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
        )}
      </div>
      <span className={`text-xs ${checked ? "text-[#D4AF37]" : "text-gray-400 group-hover:text-gray-300"}`}>
        {label}
      </span>
    </button>
  );
}

// Radio option (for single-select like age/price ranges)
function RadioOption({
  label,
  selected,
  onChange,
}: {
  label: string;
  selected: boolean;
  onChange: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onChange}
      className="flex items-center gap-2.5 py-1 cursor-pointer group w-full text-left"
    >
      <div
        className={`w-4 h-4 rounded-full border flex-shrink-0 flex items-center justify-center transition-all ${
          selected
            ? "border-[#D4AF37]"
            : "border-gray-700 group-hover:border-gray-500"
        }`}
      >
        {selected && <div className="w-2 h-2 rounded-full bg-[#D4AF37]" />}
      </div>
      <span className={`text-xs ${selected ? "text-[#D4AF37]" : "text-gray-400 group-hover:text-gray-300"}`}>
        {label}
      </span>
    </button>
  );
}

interface CompanionFiltersProps {
  filters: FilterState;
  onChange: (filters: FilterState) => void;
  resultCount: number;
}

export function CompanionFilters({ filters, onChange, resultCount }: CompanionFiltersProps) {
  const activeCount = getActiveFilterCount(filters);

  const toggleArrayFilter = (
    key: keyof Pick<FilterState, "services" | "hairColor" | "breastSize" | "dressSize" | "nationality" | "bodyType" | "availability">,
    value: string
  ) => {
    const current = filters[key];
    const next = current.includes(value)
      ? current.filter((v) => v !== value)
      : [...current, value];
    onChange({ ...filters, [key]: next });
  };

  return (
    <div className="bg-[#141414] rounded-2xl border border-gray-800">
      {/* Header */}
      <div className="px-4 py-3 bg-[#1A1A1A] border-b border-gray-800 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <SlidersHorizontal size={14} className="text-[#D4AF37]" />
          <span className="text-[10px] uppercase tracking-widest font-bold text-white">
            Filters
          </span>
          {activeCount > 0 && (
            <span className="bg-[#D4AF37] text-black text-[9px] font-bold rounded-full w-5 h-5 flex items-center justify-center">
              {activeCount}
            </span>
          )}
        </div>
        {activeCount > 0 && (
          <button
            type="button"
            onClick={() => onChange(INITIAL_FILTERS)}
            className="flex items-center gap-1 text-[9px] uppercase tracking-widest text-red-400 hover:text-red-300 transition-colors"
          >
            <X size={10} /> Clear
          </button>
        )}
      </div>

      {/* Result count */}
      <div className="px-4 py-2 border-b border-gray-800">
        <span className="text-[10px] text-gray-400 uppercase tracking-widest">
          Found <span className="text-[#D4AF37] font-bold">{resultCount}</span> companions
        </span>
      </div>

      <div className="px-3">
        {/* Price */}
        <FilterSection title="Price" defaultOpen>
          <div className="space-y-0.5">
            {PRICE_RANGES.map((range) => (
              <RadioOption
                key={range.label}
                label={range.label}
                selected={
                  filters.priceRange?.min === range.min &&
                  filters.priceRange?.max === range.max
                }
                onChange={() =>
                  onChange({
                    ...filters,
                    priceRange:
                      filters.priceRange?.min === range.min
                        ? null
                        : { min: range.min, max: range.max },
                  })
                }
              />
            ))}
          </div>
        </FilterSection>

        {/* Services */}
        <FilterSection title="Services" defaultOpen>
          <div className="space-y-0.5 max-h-48 overflow-y-auto scrollbar-thin">
            {SERVICE_OPTIONS.map((svc) => (
              <CheckOption
                key={svc}
                label={svc}
                checked={filters.services.includes(svc)}
                onChange={() => toggleArrayFilter("services", svc)}
              />
            ))}
          </div>
        </FilterSection>

        {/* Age */}
        <FilterSection title="Age" defaultOpen>
          <div className="space-y-0.5">
            {AGE_RANGES.map((range) => (
              <RadioOption
                key={range.label}
                label={range.label}
                selected={
                  filters.ageRange?.min === range.min &&
                  filters.ageRange?.max === range.max
                }
                onChange={() =>
                  onChange({
                    ...filters,
                    ageRange:
                      filters.ageRange?.min === range.min
                        ? null
                        : { min: range.min, max: range.max },
                  })
                }
              />
            ))}
          </div>
        </FilterSection>

        {/* Hair */}
        <FilterSection title="Hair">
          <div className="space-y-0.5">
            {HAIR_OPTIONS.map((h) => (
              <CheckOption
                key={h}
                label={h}
                checked={filters.hairColor.includes(h)}
                onChange={() => toggleArrayFilter("hairColor", h)}
              />
            ))}
          </div>
        </FilterSection>

        {/* Breast */}
        <FilterSection title="Breast">
          <div className="space-y-0.5">
            {BREAST_OPTIONS.map((b) => (
              <CheckOption
                key={b}
                label={b}
                checked={filters.breastSize.includes(b)}
                onChange={() => toggleArrayFilter("breastSize", b)}
              />
            ))}
          </div>
        </FilterSection>

        {/* Body Type */}
        <FilterSection title="Body Type">
          <div className="space-y-0.5">
            {BODY_TYPE_OPTIONS.map((bt) => (
              <CheckOption
                key={bt}
                label={bt}
                checked={filters.bodyType.includes(bt)}
                onChange={() => toggleArrayFilter("bodyType", bt)}
              />
            ))}
          </div>
        </FilterSection>

        {/* Dress Size */}
        <FilterSection title="Dress Size">
          <div className="space-y-0.5">
            {DRESS_OPTIONS.map((d) => (
              <CheckOption
                key={d}
                label={`Size ${d}`}
                checked={filters.dressSize.includes(d)}
                onChange={() => toggleArrayFilter("dressSize", d)}
              />
            ))}
          </div>
        </FilterSection>

        {/* Nationality */}
        <FilterSection title="Nationality">
          <div className="space-y-0.5">
            {NATIONALITY_OPTIONS.map((n) => (
              <CheckOption
                key={n}
                label={n}
                checked={filters.nationality.includes(n)}
                onChange={() => toggleArrayFilter("nationality", n)}
              />
            ))}
          </div>
        </FilterSection>

        {/* Availability */}
        <FilterSection title="Availability">
          <div className="space-y-0.5">
            {["Available Now", "Later Today", "Book Ahead"].map((a) => (
              <CheckOption
                key={a}
                label={a}
                checked={filters.availability.includes(a)}
                onChange={() => toggleArrayFilter("availability", a)}
              />
            ))}
          </div>
        </FilterSection>
      </div>
    </div>
  );
}

// Mobile filter trigger button
export function MobileFilterButton({
  activeCount,
  onClick,
}: {
  activeCount: number;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="lg:hidden flex items-center gap-2 px-4 py-2.5 bg-[#141414] border border-gray-800 rounded-full text-[10px] uppercase tracking-widest font-bold text-gray-300 hover:border-[#D4AF37]/40 transition-all"
    >
      <SlidersHorizontal size={12} className="text-[#D4AF37]" />
      Filters
      {activeCount > 0 && (
        <span className="bg-[#D4AF37] text-black text-[9px] font-bold rounded-full w-5 h-5 flex items-center justify-center">
          {activeCount}
        </span>
      )}
    </button>
  );
}

export { INITIAL_FILTERS };
